import { useSyncExternalStore, type ReactNode } from 'react';
import { Bell, CheckCheck, Mail, Megaphone, MessageSquare } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { Badge } from '@/shared/ui/badge';
import { Popover, PopoverContent } from '@/shared/ui/popover';
import { getNotices, markAllNoticesRead, markNoticeRead, subscribeNotices } from '../model/inbox';
import type { NoticeKind, UserNotice } from '../model/notices';
import { useUnreadNotices } from '../model/use-unread-notices';

const CHANNEL_LABEL = { email: 'E-mail', sms: 'SMS' } as const;

function formatNoticeTime(iso: string): string {
  const time = new Date(iso).getTime();
  if (Number.isNaN(time)) return '';
  const minutes = Math.floor(Math.max(0, Date.now() - time) / 60_000);
  if (minutes < 1) return 'agora';
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `há ${days} d`;
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

function noticeIcon(notice: UserNotice) {
  if (notice.kind === 'campaign-cut') return Megaphone;
  return notice.channel === 'sms' ? MessageSquare : Mail;
}

function noticeTone(kind: NoticeKind): string {
  if (kind === 'quota-exhausted') return 'bg-destructive/12 text-destructive';
  if (kind === 'quota-approaching') return 'bg-warning/15 text-warning';
  return 'bg-primary/12 text-primary';
}

function NoticeRow({ notice }: { notice: UserNotice }) {
  const Icon = noticeIcon(notice);
  return (
    <li>
      <button
        type="button"
        className={cn(
          'flex w-full gap-3 px-4 py-3.5 text-left transition-colors hover:bg-accent/70',
          !notice.read && 'bg-primary/[0.06]',
        )}
        onClick={() => markNoticeRead(notice.id)}
      >
        <span
          className={cn(
            'grid size-9 shrink-0 place-items-center rounded-xl',
            noticeTone(notice.kind),
          )}
        >
          <Icon className="size-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-start justify-between gap-3">
            <span
              className={cn(
                'text-sm leading-snug',
                notice.read ? 'font-medium text-foreground/80' : 'font-semibold',
              )}
            >
              {notice.title}
            </span>
            {!notice.read ? (
              <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
            ) : null}
          </span>
          <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
            {notice.body}
          </span>
          <span className="mt-2 flex items-center gap-2">
            <Badge
              variant={
                notice.kind === 'quota-exhausted'
                  ? 'destructive'
                  : notice.kind === 'quota-approaching'
                    ? 'warning'
                    : 'default'
              }
            >
              {CHANNEL_LABEL[notice.channel]}
            </Badge>
            <time className="text-[11px] text-muted-foreground" dateTime={notice.createdAt}>
              {formatNoticeTime(notice.createdAt)}
            </time>
          </span>
        </span>
      </button>
    </li>
  );
}

/** Contador compacto para o item de menu e para o avatar. */
export function UnreadNoticesBadge({ count, className }: { count: number; className?: string }) {
  if (count <= 0) return null;
  return (
    <span
      className={cn(
        'grid h-5 min-w-5 place-items-center rounded-full bg-primary px-1.5 text-[10px] font-semibold tabular-nums text-primary-foreground',
        className,
      )}
    >
      {count > 9 ? '9+' : count}
    </span>
  );
}

/**
 * Painel de avisos (cota de e-mail/SMS, campanhas cortadas). Abre a partir do
 * menu do usuário: quem chama controla `open` e marca o elemento de referência
 * com `PopoverAnchor` dentro de `children`.
 */
export function NoticesPopover({
  open,
  onOpenChange,
  side,
  align,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  side: 'top' | 'right';
  align: 'start' | 'end';
  children: ReactNode;
}) {
  const notices = useSyncExternalStore(subscribeNotices, getNotices, getNotices);
  const unread = useUnreadNotices();

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      {children}
      <PopoverContent
        align={align}
        side={side}
        collisionPadding={12}
        // Foco que anda fora (volta ao menu do usuário, tooltip) não fecha o painel;
        // clique fora e Esc continuam fechando.
        onFocusOutside={(event) => event.preventDefault()}
        className="!w-[min(26rem,calc(100vw-1.5rem))] overflow-hidden !border-border/80 !bg-popover p-0 shadow-glass-lg"
      >
        <div className="flex items-center gap-3 border-b border-border/60 px-4 py-3.5">
          <span className="bg-primary/12 grid size-9 shrink-0 place-items-center rounded-xl text-primary">
            <Bell className="size-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold tracking-tight">Avisos</p>
            <p className="text-xs text-muted-foreground">
              {unread > 0 ? `${unread} sem ler` : 'Cota de e-mail e SMS'}
            </p>
          </div>
          {notices.some((notice) => !notice.read) ? (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent/70 hover:text-foreground"
              onClick={markAllNoticesRead}
            >
              <CheckCheck className="size-3.5" />
              Marcar lidos
            </button>
          ) : null}
        </div>
        {notices.length === 0 ? (
          <div className="flex flex-col items-center px-6 py-10 text-center">
            <span className="grid size-12 place-items-center rounded-2xl bg-primary/10 text-primary">
              <Bell className="size-5" />
            </span>
            <p className="mt-3 text-sm font-semibold">Nada por aqui</p>
            <p className="mt-1 max-w-[18rem] text-sm leading-relaxed text-muted-foreground">
              Quando a cota apertar ou uma campanha for cortada, o aviso aparece aqui.
            </p>
          </div>
        ) : (
          <ul className="max-h-[22rem] divide-y divide-border/60 overflow-y-auto">
            {notices.map((notice) => (
              <NoticeRow key={notice.id} notice={notice} />
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
